/**
 * @fileoverview Availability key helpers for local-first cache discovery.
 *
 * WHAT THIS FILE DOES
 * - Defines the canonical availability key format (`sim:` + `yt:`).
 * - Provides helpers to build and parse availability keys safely.
 * - Centralizes validation rules for fingerprints and YouTube IDs.
 *
 * WHY THIS FILE EXISTS
 * - Availability keys are shared across extension and backend.
 * - A single source of truth prevents subtle format drift.
 * - Privacy: keys must never leak raw URLs before user intent.
 *
 * ARCHITECTURE ROLE
 * - Part of @gist-protocol/core shared utilities.
 * - Synced into the Chrome extension via scripts/sync-protocol-shared.js.
 * - Imported by backend availability endpoints and index tooling.
 *
 * CONTRACT SUMMARY
 * - sim: availability key = `sim:{fingerprint}` (64-bit hex).
 * - yt: availability key = `yt:{videoId}` (11-char YouTube ID).
 * - Parsing is strict; invalid values return null.
 *
 * DATA FLOW
 * - Extension: body -> availability fingerprint -> `sim:{fingerprint}`.
 * - YouTube: videoId -> `yt:{videoId}`.
 * - Backend: parse key -> match via index (sim) or exact lookup (yt).
 *
 * CALLED BY
 * - (Phase 1+) extension local index matching.
 * - (Phase 3+) /api/availability endpoint.
 *
 * THIS MODULE CALLS
 * - SIMHASH_HEX_LENGTH from simhash.js for validation.
 *
 * ERROR HANDLING
 * - Invalid inputs return null (no throws).
 * - Callers must treat null as "not parseable."
 *
 * EDGE CASES
 * - Empty/whitespace inputs -> null.
 * - Uppercase hex fingerprints -> normalized to lowercase.
 * - Extra separators (multiple colons) -> rejected.
 *
 * NON-GOALS
 * - Does not compute fingerprints (see simhash.js).
 * - Does not validate URLs (see url-utils.js).
 * - Does not perform index lookups or Hamming distance checks.
 *
 * CHANGE SAFETY
 * - Any change here must be coordinated with index formats and backend parsing.
 *
 * SECURITY / PRIVACY
 * - Availability keys never include raw URLs or page text.
 * - sim keys are one-way hashes; yt keys are public video IDs.
 *
 * PERFORMANCE
 * - Pure string validation; runs in microseconds.
 * - Safe to call on hot paths (content scripts, service workers).
 *
 * VERSIONING
 * - Prefix changes are breaking and require index format bumps.
 * - Backward compatibility must be maintained for old clients.
 *
 * COMPATIBILITY
 * - Keys are ASCII-only for safe transport in URLs and logs.
 * - Prefixes are lowercase by contract (case-insensitive parsing).
 *
 * TESTING
 * - packages/protocol/shared/__tests__/availability-keys.test.js
 *
 * EXAMPLE
 * - buildSimAvailabilityKey('0ae0d21f497fd18f') -> 'sim:0ae0d21f497fd18f'
 * - buildYouTubeAvailabilityKey('dQw4w9WgXcQ') -> 'yt:dQw4w9WgXcQ'
 * - parseAvailabilityKey('yt:dQw4w9WgXcQ') -> { type: 'yt', videoId: 'dQw4w9WgXcQ' }
 *
 * RELATED FILES
 * - packages/protocol/shared/simhash.js
 * - README/Plans/features/auto-gist/109-availability-key-single-fingerprint-plan.md
 * - packages/chrome-plugin/scripts/sync-protocol-shared.js
 */

import { SIMHASH_HEX_LENGTH } from './simhash.js';

/** Separator used between availability key prefix and payload. */
export const AVAILABILITY_KEY_SEPARATOR = ':';

/**
 * Availability key prefixes for simhash and YouTube exact matches.
 */
export const AVAILABILITY_KEY_PREFIXES = Object.freeze({
  SIMHASH: 'sim',
  YOUTUBE: 'yt'
});

const SIMHASH_HEX_PATTERN = new RegExp(`^[0-9a-f]{${SIMHASH_HEX_LENGTH}}$`, 'i');
const YOUTUBE_VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

/**
 * @typedef {Object} AvailabilityKeyParseResultSim
 * @property {'sim'} type
 * @property {string} fingerprint
 */

/**
 * @typedef {Object} AvailabilityKeyParseResultYouTube
 * @property {'yt'} type
 * @property {string} videoId
 */

/**
 * @typedef {AvailabilityKeyParseResultSim | AvailabilityKeyParseResultYouTube} AvailabilityKeyParseResult
 */

/**
 * Normalizes a SimHash hex string for availability keys.
 *
 * @param {string | null | undefined} value
 * @returns {string | null} Lowercased 16-char hex string or null when invalid.
 */
function normalizeSimHashHex(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim().toLowerCase();
  if (!SIMHASH_HEX_PATTERN.test(trimmed)) {
    return null;
  }

  return trimmed;
}

/**
 * Normalizes a YouTube video ID for availability keys.
 *
 * @param {string | null | undefined} value
 * @returns {string | null} Video ID or null when invalid.
 */
function normalizeYouTubeVideoId(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!YOUTUBE_VIDEO_ID_PATTERN.test(trimmed)) {
    return null;
  }

  return trimmed;
}

/**
 * Builds a simhash availability key.
 *
 * @param {string | null | undefined} fingerprint
 * @returns {string | null} Availability key or null when invalid.
 */
export function buildSimAvailabilityKey(fingerprint) {
  const normalized = normalizeSimHashHex(fingerprint);
  if (!normalized) {
    return null;
  }

  return `${AVAILABILITY_KEY_PREFIXES.SIMHASH}${AVAILABILITY_KEY_SEPARATOR}${normalized}`;
}

/**
 * Builds a YouTube availability key.
 *
 * @param {string | null | undefined} videoId
 * @returns {string | null} Availability key or null when invalid.
 */
export function buildYouTubeAvailabilityKey(videoId) {
  const normalized = normalizeYouTubeVideoId(videoId);
  if (!normalized) {
    return null;
  }

  return `${AVAILABILITY_KEY_PREFIXES.YOUTUBE}${AVAILABILITY_KEY_SEPARATOR}${normalized}`;
}

/**
 * Parses an availability key into a typed payload.
 *
 * @param {string | null | undefined} key
 * @returns {AvailabilityKeyParseResult | null} Parsed result or null when invalid.
 */
export function parseAvailabilityKey(key) {
  if (typeof key !== 'string') {
    return null;
  }

  const trimmed = key.trim();
  const separatorIndex = trimmed.indexOf(AVAILABILITY_KEY_SEPARATOR);
  if (separatorIndex <= 0) {
    return null;
  }

  const prefix = trimmed.slice(0, separatorIndex).toLowerCase();
  const payload = trimmed.slice(separatorIndex + 1);
  if (!payload) {
    return null;
  }

  if (prefix === AVAILABILITY_KEY_PREFIXES.SIMHASH) {
    const fingerprint = normalizeSimHashHex(payload);
    return fingerprint ? { type: 'sim', fingerprint } : null;
  }

  if (prefix === AVAILABILITY_KEY_PREFIXES.YOUTUBE) {
    const videoId = normalizeYouTubeVideoId(payload);
    return videoId ? { type: 'yt', videoId } : null;
  }

  return null;
}
